import { observable, makeObservable, action, runInAction } from "mobx";
import ProjectManagementAPI from "../../api/ProjectManagementAPI";
import {
  ProjectSourcingProduct,
  SourcingVariantRow,
} from "../../models/ProjectManagement/ProjectSourcingProduct";
import { ModuleWiseProgress } from "../../models/ProjectManagement/ModuleWiseProgress";
import {
  ProductVariantData,
  PurchaseRequestProductRequestBody,
} from "../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { PurchaseRequestBody } from "../../models/PurchaseRequest/PurchaseRequestBody";
import ProjectSourcingOpsAPI from "../../api/ProjectSourcingOpsAPI";
import { ProductSourcingDetails } from "../../models/ProjectManagement/ProjectSourcingDetails";
import { PrintingType } from "../../models/ProjectManagement/PrintingType";
import { APIBlobResponse } from "../../models/NetworkResponse";
import { PurchaseRequestProductInsightsResponse } from "../../models/PurchaseRequest/PurchaseRequestProductInsights";

export class ProjectSourcingOpsStore {
  products: ProjectSourcingProduct[];
  tasks: ModuleWiseProgress[];
  projectManagementAPI: ProjectManagementAPI;
  projectSourcingOpsAPI: ProjectSourcingOpsAPI;
  purchaseRequestProducts: PurchaseRequestProductRequestBody[];
  purchaseRequestBody: PurchaseRequestBody;
  originalProductDetails: ProductSourcingDetails | null;
  productDetails: ProductSourcingDetails | null;
  printingTypes: PrintingType[];
  selectedMockupId: number | null = null;
  sourcingProgress: number | null = null;
  approvalImages: File[] = [];
  approvalNote: string = "";
  rejectionNote: string = "";
  purchaseRequestProductInsights: PurchaseRequestProductInsightsResponse;
  selectedProductVariants: Map<number, ProductVariantData[]>;

  constructor() {
    this.products = [];
    this.tasks = [];
    this.projectManagementAPI = new ProjectManagementAPI();
    this.projectSourcingOpsAPI = new ProjectSourcingOpsAPI();
    this.purchaseRequestProducts = [];
    this.purchaseRequestBody = {} as PurchaseRequestBody;
    this.originalProductDetails = {} as ProductSourcingDetails;
    this.productDetails = {} as ProductSourcingDetails;
    this.printingTypes = [];
    this.selectedMockupId = null;
    this.sourcingProgress = null;
    this.approvalImages = [];
    this.approvalNote = "";
    this.rejectionNote = "";
    this.purchaseRequestProductInsights = {};
    this.selectedProductVariants = new Map();

    makeObservable(this, {
      products: observable,
      tasks: observable,
      purchaseRequestProducts: observable,
      purchaseRequestBody: observable,
      sourcingProgress: observable,
      approvalImages: observable,
      approvalNote: observable,
      rejectionNote: observable,
      selectedProductVariants: observable,
      handleSingleSelectProductForPr: action,
      handleSelectAllProductsForPr: action,
      updateCity: action,
      productDetails: observable,
      getSourcingOpsProductDetails: action,
      addProductVariant: action,
      removeProductVariant: action,
      updateProductVariant: action,
      calculatePurchaseRequestTotalSum: action,
      getAvailableOptions: action,
      findMatchingVariant: action,
      setSelectedMockupId: action,
      clearExistingPrProducts: action,
      setApprovalImages: action,
      setApprovalNotes: action,
      clearApprovalData: action,
      setRejectionNotes: action,
      clearRejectionData: action,
      purchaseRequestProductInsights: observable,
      updateOtherCharges: action,
      updatePrProductDescription: action,
    });
  }

  getProjectSourcingOpsProducts = async (projectId: number) => {
    try {
      const response =
        await this.projectSourcingOpsAPI.getSourcingOpsProducts(projectId);

      if (response?.products.length > 0) {
        this.products = response?.products || [];
        this.tasks = response?.tasks || [];
        this.products.forEach((product) => {
          product.tasks = product.tasks || [];

          const existingTaskMap = new Map(
            product.tasks.map((task) => [task.task_id, task]),
          );

          product.tasks = this.tasks.map((task) => {
            const taskId = task.task_id ?? task.id;
            return (
              existingTaskMap.get(taskId) || {
                module_id: task.module_id,
                task_id: taskId,
                task: task.task,
                product_id: product.id,
                active: 0,
                created_date: new Date(),
                modified_date: new Date(),
                order_id: task.order_id,
                module_name: task.module_name,
              }
            );
          });
        });
      } else {
        this.products = [];
        this.tasks = [];
      }
      this.sourcingProgress = response?.progress || null;
    } catch (error: any) {
      throw error;
    }
  };

  generateVariantDescription(variants?: SourcingVariantRow[]): string {
    if (!variants || variants.length === 0) return "";
    return variants.map((v) => `${v.sku} (${v.quantity})`).join(", ");
  }

  handleSingleSelectProductForPr = (product: ProjectSourcingProduct) => {
    const prProducts = this.purchaseRequestProducts;
    const exists = prProducts?.some((p) => p.id === product.id);

    const productWithDescription: PurchaseRequestProductRequestBody = {
      id: product.id,
      description: product.description,
      quantity: product.quantity,
      sku: product.sku,
      rate: product.cost_price,
      cost_price: product.cost_price,
      variant_ids: product.variants?.map((v) => v.id),
      gst_percentage: product.gst_percentage,
    };

    this.purchaseRequestProducts = exists
      ? prProducts?.filter((p) => p.id !== product.id) || []
      : [...(prProducts || []), productWithDescription];

    this.purchaseRequestBody.pr_products = this.purchaseRequestProducts;
  };

  handleSelectAllProductsForPr = () => {
    this.purchaseRequestProducts =
      (this.purchaseRequestProducts?.length ?? 0) > 0
        ? []
        : this.products.map((product) => ({
            id: product.id,
            description: product.description,
            quantity: product.quantity,
            sku: product.sku,
            rate: product.cost_price,
            cost_price: product.cost_price,
            variant_ids: product.variants?.map((v) => v.id),
            gst_percentage: product.gst_percentage,
          }));
    this.purchaseRequestBody.pr_products = this.purchaseRequestProducts;
  };

  updatePrProductDescription = (productId: number, description: string) => {
    const index = this.purchaseRequestProducts.findIndex(
      (product) => Number(product.id) === productId,
    );

    if (index !== -1) {
      this.purchaseRequestProducts[index] = {
        ...this.purchaseRequestProducts[index],
        description,
      };
    }
  };

  getPurchaseRequestProductsInsight = async (
    projectId: number,
    productIds: number[],
  ) => {
    try {
      const response =
        await this.projectManagementAPI.getPurchaseRequestProductInsights(
          projectId,
          productIds,
        );

      // Insights are stored separately in purchaseRequestProductInsights

      if (response) {
        this.purchaseRequestProductInsights = response;
      }
    } catch (error: any) {
      throw error;
    }
  };

  downloadPurchaseOrder = async (po_id: number): Promise<APIBlobResponse> => {
    try {
      const response = (await this.projectSourcingOpsAPI.downloadPO(
        po_id,
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  clearExistingPrProducts = () => {
    this.purchaseRequestProducts = [];
    this.selectedProductVariants.clear();
    this.purchaseRequestBody = {
      vendor_id: undefined,
      warehouse_id: undefined,
      expected_delivery_date: undefined,
      is_urgent_sourcing_request: false,
      pr_products: [],
      vendor_payment_term: undefined,
      subtotal: undefined,
      total_tax: undefined,
      total_amount: undefined,
      city_id: undefined,
      city_name: undefined,
      warehouse_name: undefined,
      vendor_name: undefined,
      vendor_email: undefined,
      emails: [],
      other_charges: undefined,
    };
  };

  createPurchaseRequest = async (
    projectId: number,
    vendorId: number,
    prProducts: PurchaseRequestProductRequestBody[],
    expectedDeliveryDate: Date,
    isUrgentSourcingRequest: boolean,
    warehouseId?: string,
    otherCharges?: number | null,
  ) => {
    try {
      await this.projectManagementAPI.createPurchaseRequest(
        projectId,
        vendorId,
        prProducts,
        expectedDeliveryDate,
        isUrgentSourcingRequest,
        warehouseId,
        otherCharges,
      );
    } catch (error: any) {
      throw error;
    }
  };

  savePrDetails = (prDetails: PurchaseRequestBody) => {
    this.purchaseRequestBody = {
      ...this.purchaseRequestBody,
      ...prDetails,
    };
  };

  updateVendor = (id?: string, value?: string) => {
    if (id) {
      this.purchaseRequestBody = {
        ...this.purchaseRequestBody,
        vendor_id: Number(id),
        vendor_name: value,
      };
    } else {
      this.purchaseRequestBody = {
        ...this.purchaseRequestBody,
        vendor_id: undefined,
        vendor_name: undefined,
      };
    }
  };

  updateCity = (id?: string, value?: string) => {
    if (id) {
      this.purchaseRequestBody = {
        ...this.purchaseRequestBody,
        city_id: id,
        city_name: value,
      };
    } else {
      this.purchaseRequestBody = {
        ...this.purchaseRequestBody,
        city_id: undefined,
        city_name: undefined,
      };
    }
  };

  updateWarehouse = (id?: string, value?: string) => {
    if (id) {
      this.purchaseRequestBody = {
        ...this.purchaseRequestBody,
        warehouse_id: id,
        warehouse_name: value,
      };
    } else {
      this.purchaseRequestBody = {
        ...this.purchaseRequestBody,
        warehouse_id: undefined,
        warehouse_name: undefined,
      };
    }
  };

  updateOtherCharges = (value: number | undefined) => {
    this.purchaseRequestBody = {
      ...this.purchaseRequestBody,
      other_charges: value,
    };
  };

  getSourcingOpsProductDetails = async (
    projectId: number,
    productId: number,
  ) => {
    try {
      const response =
        await this.projectSourcingOpsAPI.getSourcingOpsProductsDetails(
          projectId,
          productId,
        );

      if (response) {
        runInAction(() => {
          this.originalProductDetails = response;
          this.productDetails = { ...response };
        });
      } else {
        this.originalProductDetails = {} as ProductSourcingDetails;
        this.productDetails = {} as ProductSourcingDetails;
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateProductDetails = <K extends keyof ProductSourcingDetails>(
    key: K,
    value: Partial<ProductSourcingDetails[K]>,
  ) => {
    runInAction(() => {
      if (!this.productDetails) return;

      const currentValue = this.productDetails[key];

      if (currentValue && typeof currentValue === "object") {
        this.productDetails[key] = {
          ...currentValue,
          ...value,
        } as ProductSourcingDetails[K];
      } else {
        this.productDetails[key] = value as ProductSourcingDetails[K];
      }
    });
  };

  saveFilesForPrinting = async (
    projectId: number,
    productId: number,
    imageUrls: string[],
  ) => {
    try {
      const response = await this.projectSourcingOpsAPI.saveFilesForPrinting(
        projectId,
        productId,
        imageUrls,
      );

      if (response) {
        runInAction(() => {
          this.productDetails = { ...response };
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  deleteFilesForPrinting = async (
    projectId: number,
    productId: number,
    id: number,
  ) => {
    try {
      const response = await this.projectSourcingOpsAPI.deleteFilesForPrinting(
        projectId,
        productId,
        id,
      );

      if (response) {
        runInAction(() => {
          this.productDetails = { ...response };
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  saveSourcingOpsBasicDetails = async (
    projectId: number,
    productId: number,
  ) => {
    try {
      const response =
        await this.projectSourcingOpsAPI.saveSourcingOpsBasicDetails(
          projectId,
          productId,
          this.productDetails,
        );
      if (response) {
        runInAction(() => {
          this.productDetails = { ...response };
        });
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  saveSourcingOpsAdditionalDetails = async (
    projectId: number,
    productId: number,
  ) => {
    try {
      const response =
        await this.projectSourcingOpsAPI.saveSourcingOpsOtherDetails(
          projectId,
          productId,
          this.productDetails,
        );

      if (response) {
        runInAction(() => {
          this.productDetails = { ...response };
        });
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getTypesOfPrinting = async (productId?: number) => {
    try {
      const response =
        await this.projectSourcingOpsAPI.getTypesOfPrinting(productId);
      if (response) {
        runInAction(() => {
          this.printingTypes = response;
        });
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteVariants = async (projectId: number, productId: number, id: number) => {
    try {
      const response = await this.projectSourcingOpsAPI.deleteVariants(
        projectId,
        productId,
        id,
      );
      if (response) {
        runInAction(() => {
          this.productDetails = { ...response };
        });
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  setSelectedMockupId(id: number | null) {
    this.selectedMockupId = id;
  }

  updateMockupPriority = async (
    projectId: number,
    productId: number,
    mockupId: number,
  ) => {
    try {
      const response = await this.projectSourcingOpsAPI.updateMockupPriority(
        projectId,
        productId,
        mockupId,
      );

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  sendMailToClient = async (projectId: number) => {
    try {
      const response =
        await this.projectSourcingOpsAPI.sendMailToClient(projectId);

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  sendMailToPrinter = async (projectId: number, productId: number) => {
    try {
      const response = await this.projectSourcingOpsAPI.sendMailToPrinter(
        projectId,
        productId,
      );

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  editCustomization = async (
    projectId: number,
    productId: number,
    customization: boolean,
  ) => {
    try {
      const response = await this.projectSourcingOpsAPI.editCustomization(
        projectId,
        productId,
        customization,
      );

      if (response) {
        runInAction(() => {
          this.productDetails = { ...response };
        });
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  checkProductIndex = (productId: number): number => {
    if (!this.products || this.products.length === 0) return -1;

    const productList = this.products;
    const currentIndex = productList.findIndex(
      (product) => product.id === productId,
    );

    return currentIndex;
  };

  setApprovalImages = (images: File[]) => {
    this.approvalImages = images;
  };

  setApprovalNotes = (approval_note: string) => {
    this.approvalNote = approval_note;
  };

  clearApprovalData = () => {
    this.approvalImages = [];
    this.approvalNote = "";
  };

  setRejectionNotes = (rejection_note: string) => {
    this.rejectionNote = rejection_note;
  };

  clearRejectionData = () => {
    this.rejectionNote = "";
  };

  //Create Purchase Request Product Variant Management Methods
  addProductVariant = (productId: number) => {
    // Find the product to get its default cost price
    const product = this.products.find((p) => p.id === productId);
    const defaultCostPrice = product?.cost_price || 0;

    const newVariant: ProductVariantData = {
      id: `${productId}_${Date.now()}`,
      productId,
      selectedOptions: {},
      quantity: null,
      costPrice: defaultCostPrice,
    };

    const existingVariants = this.selectedProductVariants.get(productId) || [];
    this.selectedProductVariants.set(productId, [
      ...existingVariants,
      newVariant,
    ]);
    this.calculatePurchaseRequestTotalSum();
  };

  removeProductVariant = (productId: number, variantId: string) => {
    const existingVariants = this.selectedProductVariants.get(productId) || [];
    const filteredVariants = existingVariants.filter((v) => v.id !== variantId);
    this.selectedProductVariants.set(productId, filteredVariants);
    this.calculatePurchaseRequestTotalSum();
  };

  updateProductVariant = (
    productId: number,
    variantId: string,
    field: keyof ProductVariantData,
    value: any,
  ) => {
    const existingVariants = this.selectedProductVariants.get(productId) || [];
    const updatedVariants = existingVariants.map((v) =>
      v.id === variantId ? { ...v, [field]: value } : v,
    );

    this.selectedProductVariants.set(productId, updatedVariants);
    this.calculatePurchaseRequestTotalSum();
  };

  calculatePurchaseRequestTotalSum = () => {
    let subTotal = 0;
    let tax = 0;

    // Calculate from variants
    this.selectedProductVariants.forEach((variants) => {
      variants.forEach((variant) => {
        subTotal += (variant.costPrice || 0) * (variant.quantity || 0);
      });
    });

    this.purchaseRequestProducts.forEach((product) => {
      if (!this.selectedProductVariants.has(product.id || 0)) {
        subTotal += (product.cost_price || 0) * (product.quantity || 0);
      }
    });

    this.purchaseRequestBody.subtotal = subTotal;
    this.purchaseRequestBody.total_tax = tax;
    this.purchaseRequestBody.total_amount =
      subTotal + tax + (this.purchaseRequestBody.other_charges || 0);
  };

  getAvailableOptions = (product: ProjectSourcingProduct) => {
    if (!product.product_variants || product.product_variants.length === 0) {
      return [];
    }

    const optionsMap = new Map<
      number,
      { values: Set<string>; optionName: string }
    >();

    // Filter to only show Size and Color variants
    const filteredVariants = product.product_variants.filter((variant) => {
      const optionName = variant.options_name?.toLowerCase() || "";
      const optionId = variant.options_id;
      // Include only Size (options_id: 1 or options_name: "Size") and Color (options_id: 2 or options_name: "Color")
      return (
        optionName === "size" ||
        optionName === "color" ||
        optionId === 1 ||
        optionId === 2
      );
    });

    filteredVariants.forEach((variant) => {
      const optionId = variant.options_id;
      if (!optionsMap.has(optionId)) {
        optionsMap.set(optionId, {
          values: new Set(),
          optionName: variant.options_name || "",
        });
      }
      // Use title if value is null/empty, as title contains the actual variant name (e.g., "M", "L", "Black", "Blue")
      const variantValue = variant.value || variant.title || variant.sku || "";
      if (variantValue) {
        optionsMap.get(optionId)!.values.add(variantValue);
      }
    });

    return Array.from(optionsMap.entries()).map(([optionId, data]) => ({
      optionId,
      optionName: data.optionName,
      values: Array.from(data.values).map((value) => ({ id: value, value })),
    }));
  };

  findMatchingVariant = (
    product: ProjectSourcingProduct,
    selectedOptions: { [key: string]: string },
  ) => {
    if (!product.product_variants) return null;

    return product.product_variants.find((variant) => {
      return Object.entries(selectedOptions).every(
        ([optionId, selectedValue]) => {
          // selectedOptions keys are options_id (as strings from option.optionId)
          // Compare using title first since value is often null in API responses
          return (
            variant.options_id === Number(optionId) &&
            (variant.title === selectedValue ||
              variant.value === selectedValue ||
              variant.sku === selectedValue)
          );
        },
      );
    });
  };
}
